import React, { useState } from "react";
import { X, Plus } from "lucide-react";
import useEscape from "../lib/useEscape";
import api, { apiErrorMessage } from "../lib/api";
import { toast } from "sonner";

/**
 * "Suggest a product" modal — shown when a logged-in user's search returns 0 results.
 * Sends a product request to admin for approval.
 */
export default function SuggestProductModal({ open, defaultQuery, onClose, onDone }) {
  useEscape(open, onClose);
  const [product, setProduct] = useState(defaultQuery || "");
  const [brand, setBrand] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (open) setProduct(defaultQuery || ""); }, [open, defaultQuery]);

  if (!open) return null;

  const submit = async () => {
    if (!product.trim() || !brand.trim()) {
      toast.error("Product and Brand are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/product-requests", {
        product: product.trim(),
        brand: brand.trim(),
        reason: reason.trim(),
      });
      toast.success("Request sent. An admin will review it shortly.");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to submit request"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-md rounded-none border border-border" onClick={(e) => e.stopPropagation()} data-testid="suggest-product-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-[#FFBE06]/15">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Plus size={18} /> Suggest a product</h3>
            <div className="text-xs text-muted-foreground">Don't see what you want? Tell us — an admin will list it after review.</div>
          </div>
          <button onClick={onClose} data-testid="suggest-product-close"><X size={18} /></button>
        </div>

        <div className="px-5 py-5 space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Product *</label>
            <input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. iPhone 18 Pro" className="input-bhega mt-1" data-testid="suggest-product-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brand *</label>
            <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Apple" className="input-bhega mt-1" data-testid="suggest-product-brand" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Why do you want this listed? (optional)</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A short note helps admins prioritise…" className="input-bhega mt-1" data-testid="suggest-product-reason" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="suggest-product-submit">{busy ? "Submitting…" : "Submit request"}</button>
        </div>
      </div>
    </div>
  );
}
